import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
    inject
} from '@angular/core';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { Client } from '@df/core/service/client';
import { DfEventService } from '@df/core/service/df-event';
import { Events } from '@df/core/service/event';
import { GtmService } from '@df/module-gtm/gtm.service';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { Subscription } from 'rxjs/internal/Subscription';
import { DialogComponent } from '../../../../common/dialog/dialog.component';
import { DIALOG_EVENT, DialogEventType } from '../../../../common/dialog/dialog.interface';
import { DialogOpenDirective } from '../../../../common/dialog/open/dialog-open.directive';
import { ActionComponent } from '../../../../ui/api/action.component';
import { FilterAttributeOptionComponent } from '../filter/attribute-option/filter-attribute-option.component';
import { FilterAttributeRangeComponent } from '../filter/attribute-range/filter-attribute-range.component';
import { FilterStockComponent } from '../filter/stock/filter-stock.component';
import { OverlayComponent } from './../../../../ui/common/overlay/overlay.component';

@Component({
    selector: 'filters',
    templateUrl: './filters.component.html',
    styleUrl: './filters.component.scss',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        ActionComponent,
        CommonModule,
        DialogComponent,
        DialogOpenDirective,
        FilterAttributeOptionComponent,
        FilterAttributeRangeComponent,
        FilterStockComponent,
        GridModule,
        OverlayComponent,
        SizeClassDirective,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FiltersComponent implements OnInit, OnDestroy {
    readonly dialogId = 'filters-dialog';

    protected subscriptions = new Subscription();
    protected events = inject(Events);
    protected readonly client = inject(Client);
    private gtmService = inject(GtmService);

    /**
     * Listing setter
     */
    @Input()
    set listing(listing: ProductListing) {
        this.unsubscribeListing();

        this._listing = listing;

        this.listingSubscription = listing?.subscribe(() => this.detectChanges());
    }
    get listing(): ProductListing {
        return this._listing!;
    }

    @Output()
    filtersOpen = new EventEmitter();

    @Output()
    filtersClose = new EventEmitter();

    private initiated = false;
    private dfEventService = inject(DfEventService);

    private listingSubscription?: Subscription;

    /**
     * Listing model
     */
    private _listing?: ProductListing;

    constructor(
        protected cd: ChangeDetectorRef
    ) {
        cd.detach();

        this.subscriptions.add(this.events.on('search.open').subscribe(() => this.closeModal()));

        // Listen for dialog events to emit component events
        this.subscriptions.add(
            this.dfEventService.success(event => {
                if (event.id === DIALOG_EVENT && event.data.id === this.dialogId) {
                    if (event.data.type === DialogEventType.Close) {
                        this.filtersClose.emit();
                    }
                }
            })
        );
    }

    ngOnInit(): void {
        this.initiated = true;
        this.detectChanges();
    }

    ngOnDestroy(): void {
        this.unsubscribeListing();
        this.subscriptions.unsubscribe();
    }

    getFilters() {
        return this.listing.getFiltersWithManyCurrentMatches();
    }

    openModal() {
        this.dfEventService.dispatch({
            id: DIALOG_EVENT,
            data: {
                type: DialogEventType.Open,
                id: this.dialogId
            },
            action: () => {
                this.sendGtmEvent('openFilters');
                this.filtersOpen.emit();
            }
        });
    }

    closeModal() {
        this.dfEventService.dispatch({
            id: DIALOG_EVENT,
            data: {
                type: DialogEventType.Close,
                id: this.dialogId
            }
        });

        this.sendGtmEvent('closeFilters');
        this.filtersClose.emit();
    }

    clearFilters() {
        this.listing.resetFilters();
        this.closeModal();
    }

    private detectChanges() {
        if (this.initiated) {
            this.cd.detectChanges();
        }
    }

    private sendGtmEvent(event: string): void {
        this.gtmService.push({ event });
    }

    /**
     * Unsubscribes listing changes if subscription exists
     */
    private unsubscribeListing() {
        if (this.listingSubscription) {
            this.listingSubscription.unsubscribe();
        }
    }
}
